// Settings · Data Sources.
//
// One form, one list. Add a Metabase URL + an API key (or session creds);
// the Rust side persists the source to ~/.peppermint-suite/config.json and
// hands back an auth-stripped record for display. The "Test connection"
// button round-trips through the Rust client so the user sees a real
// auth result before saving.

import { FormEvent, useEffect, useState } from "react";

import {
  deleteDataSource,
  listDataSources,
  saveDataSource,
  testDataSource,
  testSavedDataSource,
} from "../lib/data-sources";
import type {
  AuthSpecWire,
  DataSourceSafe,
  DataSourceSpec,
  TestConnectionInfo,
} from "../lib/types";

interface Draft {
  label: string;
  url: string;
  default_database_id: string;
  auth_kind: "api_key" | "session";
  api_key: string;
  email: string;
  password: string;
}

const EMPTY_DRAFT: Draft = {
  label: "VFD Metabase",
  url: "",
  default_database_id: "",
  auth_kind: "api_key",
  api_key: "",
  email: "",
  password: "",
};

export function SettingsDataSourcesPage() {
  const [sources, setSources] = useState<DataSourceSafe[] | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      setSources(await listDataSources());
    } catch (e) {
      setError(String(e));
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const buildSpec = (): DataSourceSpec => {
    const auth: AuthSpecWire =
      draft.auth_kind === "api_key"
        ? { kind: "api_key", key: draft.api_key.trim() }
        : { kind: "session", email: draft.email.trim(), password: draft.password };
    return {
      id: "",
      label: draft.label.trim() || "Metabase",
      kind: {
        kind: "metabase",
        url: draft.url.trim().replace(/\/$/, ""),
        default_database_id: draft.default_database_id
          ? Number(draft.default_database_id)
          : undefined,
      },
      auth,
    };
  };

  const onTest = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setTestResult(null);
    setTesting(true);
    try {
      const info = await testDataSource(buildSpec());
      setTestResult(info);
    } catch (err) {
      setError(String(err));
    } finally {
      setTesting(false);
    }
  };

  const onSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await saveDataSource(buildSpec());
      setDraft(EMPTY_DRAFT);
      setTestResult(null);
      await refresh();
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: string) => {
    setError(null);
    try {
      await deleteDataSource(id);
      await refresh();
    } catch (err) {
      setError(String(err));
    }
  };

  const onRetest = async (id: string, label: string) => {
    setError(null);
    setTestResult(null);
    try {
      const info = await testSavedDataSource(id);
      setTestResult({ ...info, name: info.name ? `${info.name} (${label})` : label });
    } catch (err) {
      setError(`${label}: ${err}`);
    }
  };

  // Field-level validation that drives Test/Save button disabled state.
  // Mirrors the Rust-side validate_source() so the user gets feedback before
  // a round-trip.
  const formIssue = ((): string | null => {
    const u = draft.url.trim();
    if (!u) return "Paste your Metabase URL";
    if (!u.startsWith("http://") && !u.startsWith("https://"))
      return "URL must start with http:// or https://";
    if (draft.auth_kind === "api_key" && !draft.api_key.trim()) return "API key is empty";
    if (
      draft.auth_kind === "session" &&
      (!draft.email.trim() || !draft.password)
    )
      return "email and password required";
    return null;
  })();

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__meta">SETTINGS · DATA SOURCES</div>
          <h1>Data sources</h1>
        </div>
      </div>

      <div
        className="cols"
        style={{ gridTemplateColumns: "1.1fr 1fr", alignItems: "start" }}
      >
        {/* Add / edit form */}
        <div className="card" style={{ padding: 22 }}>
          <div className="card__tag" style={{ marginBottom: 4 }}>
            ADD SOURCE
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Connect Metabase</h3>

          <form onSubmit={onTest} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="Label">
              <input
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="VFD Metabase"
                style={inputStyle}
              />
            </Field>
            <Field label="URL">
              <input
                value={draft.url}
                onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                placeholder="https://metabase.vfdgroup.com"
                style={inputStyle}
              />
            </Field>
            <Field label="Default database id (optional)">
              <input
                value={draft.default_database_id}
                onChange={(e) =>
                  setDraft({ ...draft, default_database_id: e.target.value.replace(/[^\d]/g, "") })
                }
                placeholder="133"
                style={inputStyle}
              />
            </Field>
            <Field label="Auth">
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className={
                    draft.auth_kind === "api_key" ? "btn btn--primary" : "btn btn--ghost"
                  }
                  onClick={() => setDraft({ ...draft, auth_kind: "api_key" })}
                  style={{ flex: 1, fontSize: 12 }}
                >
                  API key
                </button>
                <button
                  type="button"
                  className={
                    draft.auth_kind === "session" ? "btn btn--primary" : "btn btn--ghost"
                  }
                  onClick={() => setDraft({ ...draft, auth_kind: "session" })}
                  style={{ flex: 1, fontSize: 12 }}
                >
                  Email + password
                </button>
              </div>
            </Field>

            {draft.auth_kind === "api_key" ? (
              <Field label="API key">
                <input
                  type="password"
                  value={draft.api_key}
                  onChange={(e) => setDraft({ ...draft, api_key: e.target.value })}
                  placeholder="mb_…"
                  style={inputStyle}
                />
              </Field>
            ) : (
              <>
                <Field label="Email">
                  <input
                    value={draft.email}
                    onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                    placeholder="kabelo@kyl.solutions"
                    style={inputStyle}
                  />
                </Field>
                <Field label="Password">
                  <input
                    type="password"
                    value={draft.password}
                    onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                    style={inputStyle}
                  />
                </Field>
              </>
            )}

            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 6,
                alignItems: "center",
              }}
            >
              <button
                type="submit"
                className="btn btn--ghost"
                disabled={testing || formIssue != null}
                title={formIssue ?? undefined}
              >
                {testing ? "Testing…" : "Test connection"}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={saving || formIssue != null}
                onClick={onSave}
                title={formIssue ?? undefined}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {formIssue && (
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--ink-faint)",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {formIssue}
                </span>
              )}
            </div>

            {testResult && (
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  background: "var(--accent-soft)",
                  border: "1px solid var(--edge)",
                  fontSize: 12,
                  color: "var(--mint)",
                }}
              >
                ✓ Authenticated as <strong>{testResult.email}</strong>
                {testResult.name ? ` · ${testResult.name}` : ""}
              </div>
            )}
            {error && (
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  background: "rgba(232, 90, 159, 0.1)",
                  border: "1px solid rgba(232, 90, 159, 0.3)",
                  fontSize: 12,
                  color: "var(--coral)",
                }}
              >
                {error}
              </div>
            )}
          </form>
        </div>

        {/* Saved list */}
        <div className="card" style={{ padding: 22 }}>
          <div className="card__tag" style={{ marginBottom: 4 }}>
            CONFIGURED
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Connected sources</h3>

          {sources && sources.length === 0 && (
            <p style={{ color: "var(--ink-dim)", fontSize: 13 }}>
              No data sources yet. Add one on the left.
            </p>
          )}
          {sources && sources.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {sources.map((s) => (
                <div
                  key={s.id}
                  style={{
                    padding: "14px 16px",
                    borderRadius: 11,
                    border: "1px solid var(--edge)",
                    background: "rgba(10, 14, 20, 0.5)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{s.label}</div>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          color: "var(--ink-faint)",
                          marginTop: 4,
                        }}
                      >
                        {s.kind.kind === "metabase" ? s.kind.url : "?"} ·{" "}
                        {s.auth_kind === "api_key" ? "API key" : "session"}
                      </div>
                    </div>
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={() => onRetest(s.id, s.label)}
                      style={{ fontSize: 11, padding: "6px 12px" }}
                    >
                      Re-test
                    </button>
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={() => onDelete(s.id)}
                      style={{ fontSize: 11, padding: "6px 12px" }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          letterSpacing: "0.14em",
          color: "var(--ink-faint)",
        }}
      >
        {label.toUpperCase()}
      </span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 13,
  padding: "10px 12px",
  borderRadius: 9,
  background: "rgba(10, 14, 20, 0.7)",
  border: "1px solid var(--edge)",
  color: "var(--ink)",
  outline: "none",
};
